import React, { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  ListSubheader,
  InputAdornment,
  Divider,
  FormHelperText,
  Typography
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { Group } from '@material-ui/icons';
import DebounceTextField from './DebounceTextField';

export default function SelectWithSearch({
  value,
  options = [],
  label = '',
  searchKeys = [],
  onChange,
  disabled,
  searchPlaceHolder = 'Type to search...',
  CustomMenuItem = null,
  error = false,
  errorMessage = '',
  selectAllUsers,
  autoFocus = false
}) {
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);

  /*
   * Automatically open when autoFocus becomes true.
   *
   * Example:
   * dialog opens
   *     ↓
   * autoFocus = true
   *     ↓
   * setOpen(true)
   */
  useEffect(() => {
    if (autoFocus && !disabled) {
      setOpen(true);
    }
  }, [autoFocus, disabled]);

  const displayedOptions = useMemo(
    () =>
      options.filter(item =>
        searchKeys.some(key =>
          item[key]?.toLowerCase().includes(searchText.toLowerCase())
        )
      ),
    [searchText, options, searchKeys]
  );

  function handleChangeSearchText(e) {
    setSearchText(e.target.value);
  }

  function handleOpen() {
    if (!disabled) {
      setOpen(true);
    }
  }

  function handleClose() {
    setOpen(false);
    clearSearchText();
  }

  function clearSearchText() {
    setSearchText('');
  }

  return (
    <FormControl error={error} variant="outlined" fullWidth>
      <InputLabel id="search-select-label">{label}</InputLabel>

      <Select
        MenuProps={{
          autoFocus: false
        }}
        labelId="search-select-label"
        id="search-select"
        value={value || ''}
        label={label}
        disabled={disabled}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={onChange}
        renderValue={val =>
          options.find(item => item.value === val)?.description ||
          (selectAllUsers && 'All users selected') ||
          ''
        }
      >
        <ListSubheader disableGutters>
          <Paper
            component={Box}
            elevation={0}
            display="flex"
            justifyContent="center"
            alignItems="center"
            p={2}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <DebounceTextField
              size="small"
              autoFocus
              placeholder={searchPlaceHolder}
              fullWidth
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              onChange={handleChangeSearchText}
              onKeyDown={e => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
            />
          </Paper>
          <Divider />
        </ListSubheader>

        {Boolean(selectAllUsers) && (
          <MenuItem value={selectAllUsers}>
            <Box display="flex" gridGap="1em" alignItems="center">
              <Group
                style={{
                  height: '1.6em',
                  width: '1.6em'
                }}
              />
              <Box>
                <Typography variant="body1">Select all users</Typography>
              </Box>
            </Box>
          </MenuItem>
        )}

        {displayedOptions.map(({ value, description, ...rest }) =>
          Boolean(CustomMenuItem) ? (
            <CustomMenuItem
              key={value}
              value={value}
              description={description}
              {...rest}
            />
          ) : (
            <MenuItem key={value} value={value}>
              {description}
            </MenuItem>
          )
        )}
      </Select>
      {error && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
}
